import { AfterViewChecked, ChangeDetectorRef, Component, Inject, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
    AppNotificationService,
    AppNotificationType,
    ConfigConstants,
    CONNECTION_ERROR_CODES,
    ConnectivityService,
    ENDPOINTS,
    MaterialQuoteModel,
    OdataMaterialService,
    OnlineOnlyService,
    SynchronizationService,
    ConfigInstanceToken,
    ConfigInterface,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { of } from 'rxjs';
import { catchError, mergeMap, take, tap } from 'rxjs/operators';
import { LocalStorageSalesQuoteService } from '../services/local-storage-sales-quote.service';

@Component({
    selector: 'pace-material-quote',
    templateUrl: './material-quote.component.html',
    styleUrls: ['./material-quote.component.scss'],
    standalone: false,
})
export class MaterialQuoteComponent implements OnInit, AfterViewChecked {
    @Input()
    public model: MaterialQuoteModel;

    public formChildren: FormArray;

    // form in HTML template
    public materialQuoteForm: FormGroup;
    public isLoading = false;
    public displayTimeFields = true;
    public displayOverallDistance = true;
    public displaySeparator = true;
    public displayLText = false;
    public processIndicator: ConfigConstants.PROCESS_INDICATOR;
    public constructor(
        private readonly translate: TranslateService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly appNotificationService: AppNotificationService,
        private readonly onlineOnlyService: OnlineOnlyService,
        private readonly odataMaterialService: OdataMaterialService,
        private readonly localStorageSalesQuoteService: LocalStorageSalesQuoteService,
        private readonly modalInstance: BsModalRef,
        private readonly formBuilder: FormBuilder,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly connectivityService: ConnectivityService,
        @Inject(ConfigInstanceToken) private readonly configService: ConfigInterface,
        private readonly synchronizationService: SynchronizationService,
    ) {}

    public ngOnInit(): void {
        this.formChildren = this.formBuilder.array([]);
        this.materialQuoteForm = this.formBuilder.group({
            formChildren: this.formChildren,
        });

        this.displayOverallDistance = this.configService.getEnableMatQuoteDistance();

        const selectedQuoteType = this.model.quoteTypes.selectedEntry;
        if (selectedQuoteType && selectedQuoteType.model.value === ConfigConstants.customerDirectOrderType) {
            this.displayTimeFields = false;
            this.displayOverallDistance = false;
            this.displaySeparator = this.displayTimeFields || this.displayOverallDistance;
        } else if (selectedQuoteType && selectedQuoteType.model.value === ConfigConstants.personalQuoteType) {
            this.displayLText = true;
        }
        this.processIndicator = this.model.notificationModel
            ? this.model.notificationModel.processIndicator
            : ConfigConstants.PROCESS_INDICATOR.INVIVO;
    }

    // TODO Check with Frederic if this is a valid solution
    public ngAfterViewChecked(): void {
        this.changeDetectorRef.detectChanges();
    }

    // Send the material quote request to the backend
    public sendQuote(): void {
        const outModel = this.model.getOutModel();
        const filteredEntries = this.model.existingRecipients.value.filter(
            (materialQuote) => materialQuote.isSelected.value && !materialQuote.isHidden,
        );
        //
        // check whether the time is valid or not (should be greater than 0)
        const isValidTime = !!(this.model.timeNeed.value || this.model.timeUsed.value);

        // check whether any material is added or not
        const isMaterialadded = !!this.model.materialList.value.length;

        if (!filteredEntries.length) {
            this.appNotificationService.setAppNotification(
                AppNotificationType.WARN,
                <string>this.translate.instant('DASHBOARD_SIDEMENU_SALES_QUOTE_VALID_RECIPIENT_TITLE'),
                <string>this.translate.instant('DASHBOARD_SIDEMENU_SALES_QUOTE_VALID_RECIPIENT_BODY'),
            );
            return;
        } else if (!isValidTime && !isMaterialadded) {
            this.appNotificationService.setAppNotification(
                AppNotificationType.WARN,
                <string>this.translate.instant('DASHBOARD_SIDEMENU_SALES_QUOTE_VALID_TIME_AND_MATERIAL_TITLE'),
                <string>this.translate.instant('DASHBOARD_SIDEMENU_SALES_QUOTE_VALID_TIME_AND_MATERIAL_BODY'),
            );
            return;
        }

        this.isLoading = true;

        const request$ =
            typeof this.model.notificationModel !== 'undefined'
                ? this.odataMaterialService.saveMaterialQuote(this.model)
                : this.onlineOnlyService.post(ENDPOINTS.MaterialQuote, outModel);

        request$
            .pipe(
                take(1),
                tap(() => {
                    // Once the send order is successful the modal should not be displayed.
                    this.deleteSalesQuote();
                }),
                mergeMap(() => {
                    if (typeof this.model.notificationModel !== 'undefined') {
                        this.log.debug('MaterialQuoteComponent: sendQuote: sendOfflineData');
                        return this.synchronizationService.sendOfflineData();
                    }
                    return of(undefined);
                }),
                catchError((error) => {
                    this.isLoading = false;
                    this.log.error(
                        `MaterialQuoteComponent: sendQuote: Error ${JSON.stringify(error)}, outModel: ${JSON.stringify(
                            outModel,
                        )}`,
                    );

                    const isOnline = this.connectivityService.isInternetConnectionAvailable();
                    if (!isOnline) {
                        this.appNotificationService.setAppNotification(
                            AppNotificationType.WARN,
                            <string>this.translate.instant('DASHBOARD_SIDEMENU_SALES_QUOTE_SEND_ERROR_TITLE'),
                            <string>this.translate.instant('DASHBOARD_SIDEMENU_SALES_QUOTE_OFFLINE_ERROR_BODY'),
                        );
                    } else {
                        this.appNotificationService.setAppNotification(
                            AppNotificationType.ERROR,
                            error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                                ? <string>this.translate.instant('OFFLINE_STORE_ERROR_TITLE')
                                : <string>this.translate.instant('DASHBOARD_SIDEMENU_SALES_QUOTE_SEND_ERROR_TITLE'),
                            error === CONNECTION_ERROR_CODES.OFFLINE_STORE_SAVE_ERROR
                                ? <string>this.translate.instant('OFFLINE_STORE_ERROR_BODY')
                                : `${this.translate.instant(
                                      'DASHBOARD_SIDEMENU_SALES_QUOTE_SEND_ERROR_BODY',
                                  )} ${JSON.stringify(error)}`,
                        );
                    }
                    return of(undefined);
                }),
            )
            .subscribe(() => (this.isLoading = false));
    }

    public quoteTypeChange(): void {
        const selectedQuoteType = this.model.quoteTypes.selectedEntry.model.value;
        this.displayLText = false;
        if (selectedQuoteType === ConfigConstants.customerDirectOrderType) {
            this.displayTimeFields = false;
            this.displayOverallDistance = false;
            this.model.timeUsed.value = 0;
            this.model.timeNeed.value = 0;
            this.model.distance.value = undefined;
            this.model.lTextSet.value = '';
        } else if (selectedQuoteType === ConfigConstants.personalQuoteType) {
            if (!this.displayTimeFields) {
                // reset to default value only if it was changed to zero before by code
                this.model.timeNeed.value = 1;
            }
            this.displayLText = true;
            this.displayTimeFields = true;
            // Earlier, displayOverallDistance needs to be visible in this condition but now checking the config flag as well to enable it
            this.displayOverallDistance = this.configService.getEnableMatQuoteDistance();
        } else {
            if (!this.displayTimeFields) {
                // reset to default value only if it was changed to zero before by code
                this.model.timeNeed.value = 1;
            }
            this.displayTimeFields = true;
            // Earlier, displayOverallDistance needs to be visible in this condition but now checking the config flag as well to enable it
            this.displayOverallDistance = this.configService.getEnableMatQuoteDistance();
            this.model.lTextSet.value = '';
        }
        this.displaySeparator = this.displayTimeFields || this.displayOverallDistance;
    }

    public saveSalesQuote(): void {
        if (this.model.notificationModel) {
            this.localStorageSalesQuoteService
                .saveSalesQuoteAsync(this.model)
                .catch((e) => this.log.error(this.constructor.name, 'failed to save sales quote:', e));
        }
        this.modalInstance.hide();
    }

    public deleteSalesQuote(): void {
        if (this.model.notificationModel) {
            this.localStorageSalesQuoteService
                .removeSalesQuoteFromLocalStorageAsync(this.model.notificationModel.identifier)
                .catch((e) =>
                    this.log.error(this.constructor.name, 'failed to remove sales quote from local storage:', e),
                );
        }
        this.modalInstance.hide();
    }
}
